/**
 * 部门管理
 *
 * 部门在平台里不只是通讯录：它是**数据权限的计算基础**。
 * 「本部门及以下」这条数据权限靠物化路径（path）做前缀匹配，
 * 因此部门层级一旦建错，财务看到的数据范围就会错——页面上把这一点讲清楚，
 * 并在移动/删除部门时给出提醒。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ApartmentOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { PermissionCode, type Dept } from '@ilc/shared';
import { orgApi } from '@/api/endpoints';
import { mockDeptList, mockUserList } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { performAction } from '@/utils/performAction';

interface DeptNode extends Dept {
  children?: DeptNode[];
}

/** 扁平列表 → 树（父节点缺失的挂到根，避免部门「消失」） */
function buildTree(list: Dept[]): DeptNode[] {
  const map = new Map<string, DeptNode>();
  for (const dept of list) map.set(dept.id, { ...dept });
  const roots: DeptNode[] = [];
  for (const node of map.values()) {
    if (node.parentId && map.has(node.parentId)) {
      const parent = map.get(node.parentId);
      if (parent) {
        parent.children = parent.children ?? [];
        parent.children.push(node);
      }
    } else {
      roots.push(node);
    }
  }
  const sort = (nodes: DeptNode[]): void => {
    nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'zh-CN'));
    for (const node of nodes) if (node.children) sort(node.children);
  };
  sort(roots);
  return roots;
}

export default function Depts(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canWrite = can(PermissionCode.DEPT_WRITE);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<Dept | null>(null);
  /** 「加子部门」时预设的上级部门。作为初值的一部分参与 Form 重建 */
  const [presetParentId, setPresetParentId] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Dept | null>(null);

  const depts = useResource<Dept[]>(() => orgApi.depts(), { fallback: () => mockDeptList() });
  const users = useResource(() => orgApi.users({ page: 1, pageSize: 100 }), {
    fallback: () => mockUserList({ page: 1, pageSize: 100 }),
  });

  const tree = useMemo(() => buildTree(depts.data ?? []), [depts.data]);

  const userCountOf = (deptId: string): number =>
    (users.data?.list ?? []).filter((u) => u.deptId === deptId).length;

  const openCreate = (parent?: Dept): void => {
    setCreating(true);
    setPresetParentId(parent?.id);
  };

  /**
   * 表单初值。**不要改回命令式 setFieldsValue**。
   * 弹窗带 `destroyOnHidden`，关闭时 `<Form>` 被卸载，此刻同步赋值会被丢弃，
   * 重新挂载时又按 initialValues（空）初始化——表现为"点编辑，标题对、里面全空"。
   * 详见 `LegalWorkbench.tsx` 的说明。
   */
  const formInitialValues = useMemo(
    () =>
      editing
        ? { name: editing.name, parentId: editing.parentId ?? undefined, sortOrder: editing.sortOrder }
        : { parentId: presetParentId },
    [editing, presetParentId],
  );

  const save = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    const result = await performAction({
      action: () =>
        orgApi.createDept({
          name: String(values.name ?? ''),
          parentId: values.parentId ? String(values.parentId) : null,
          sortOrder: Number(values.sortOrder ?? 0),
        }),
      successMessage: '部门已创建',
      offline: () => undefined,
      offlineMessage: '后端部门接口尚未就绪，本次保存仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      setCreating(false);
      setEditing(null);
      form.resetFields();
      depts.reload();
    }
  };

  const removeDept = async (reason: string): Promise<void> => {
    if (!deleteTarget) return;
    const result = await performAction({
      action: () => Promise.reject(new Error('部门删除接口尚未开放')),
      successMessage: '部门已删除',
      offline: () => undefined,
      offlineMessage: `后端未提供部门删除接口；本次仅在本地演示。删除理由：${reason}`,
    });
    setDeleteTarget(null);
    if (result.ok) depts.reload();
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="部门管理"
        description="维护部门层级。部门决定「本部门及以下」数据权限的范围，也决定待办任务派发给谁。"
        extra={
          <Space>
            <Button icon={<ApartmentOutlined />} onClick={depts.reload}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} disabled={!canWrite} onClick={() => openCreate()}>
              新增部门
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="部门层级 = 数据权限范围"
        description="「本部门及以下」的数据权限按物化路径做前缀匹配（例如 /d-project 能覆盖 /d-project/d-project-1）。因此：把部门挂在错误的父节点下，会让相应角色看到过多或过少的数据；删除或移动部门前，请先确认该部门下已无用户与在办发票。"
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card size="small" title={`部门树（${(depts.data ?? []).length} 个部门）`}>
            <PageState loading={depts.loading} error={depts.error} onRetry={depts.reload} skeleton rows={6}>
              <Table<DeptNode>
                size="small"
                rowKey="id"
                pagination={false}
                defaultExpandAllRows
                dataSource={tree}
                locale={{ emptyText: '暂无部门，点击右上角「新增部门」创建' }}
                columns={[
                  {
                    title: '部门名称',
                    dataIndex: 'name',
                    render: (v: string, record) => (
                      <Space size={4}>
                        <span>{v}</span>
                        {record.children?.length ? (
                          <Tag>{record.children.length} 个下级</Tag>
                        ) : null}
                      </Space>
                    ),
                  },
                  {
                    title: '物化路径',
                    dataIndex: 'path',
                    width: 260,
                    render: (v: string) => (
                      <Tooltip title="「本部门及以下」数据权限按此路径做前缀匹配">
                        <span className="ilc-mono" style={{ fontSize: 12 }}>
                          {v}
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: '人数',
                    key: 'userCount',
                    width: 90,
                    align: 'right',
                    render: (_v, record) => {
                      const count = userCountOf(record.id);
                      return count > 0 ? <span className="ilc-num">{count}</span> : <Typography.Text type="secondary">0</Typography.Text>;
                    },
                  },
                  { title: '排序', dataIndex: 'sortOrder', width: 80, align: 'right' },
                  {
                    title: '操作',
                    key: 'action',
                    width: 200,
                    fixed: 'right',
                    render: (_v, record) => (
                      <Space size={4}>
                        <Button
                          size="small"
                          type="link"
                          icon={<PlusOutlined />}
                          disabled={!canWrite}
                          onClick={() => openCreate(record)}
                        >
                          加子部门
                        </Button>
                        <Button
                          size="small"
                          type="link"
                          icon={<EditOutlined />}
                          disabled={!canWrite}
                          /* 只设状态。这里的 setFieldsValue 会被 destroyOnHidden 后的
                             重新挂载冲掉，初值统一由 formInitialValues 提供 */
                          onClick={() => setEditing(record)}
                        >
                          编辑
                        </Button>
                        <Button
                          size="small"
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={!canWrite}
                          onClick={() => setDeleteTarget(record)}
                        >
                          删除
                        </Button>
                      </Space>
                    ),
                  },
                ]}
              />
            </PageState>
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card size="small" title="数据权限与部门的对应关系">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="仅本人">不看部门，只返回本人经手的发票</Descriptions.Item>
              <Descriptions.Item label="本部门">精确匹配本部门 ID</Descriptions.Item>
              <Descriptions.Item label="本部门（含下级）">
                按 path 前缀匹配，包含所有子孙部门
              </Descriptions.Item>
              <Descriptions.Item label="本租户">不限部门，返回租户内全部数据</Descriptions.Item>
              <Descriptions.Item label="自定义范围">在角色上指定若干部门（含下级）</Descriptions.Item>
            </Descriptions>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              上述判定全部在服务端完成；前端只做展示与提示，不做数据过滤。
            </Typography.Paragraph>
          </Card>

          <Card size="small" title="常见配置建议" style={{ marginTop: 16 }}>
            <ul style={{ margin: 0, paddingInlineStart: 18, lineHeight: 2, fontSize: 13 }}>
              <li>经办人挂到具体项目组（如「华东项目组」），数据权限用「本部门」即可。</li>
              <li>财务部建议放在租户顶层，数据权限给「本租户」，否则会漏掉其它部门的发票。</li>
              <li>法务部通常需要「本租户」的只读权限用于抽查与归档追溯。</li>
              <li>技术实施组关注接口与日志，不要给发票金额的读取权限。</li>
            </ul>
          </Card>
        </Col>
      </Row>

      <Modal
        open={creating || editing !== null}
        title={editing ? `编辑部门 ${editing.name}` : '新增部门'}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={() => void save()}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form
          key={editing?.id ?? 'create'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={formInitialValues}
        >
          <Form.Item name="name" label="部门名称" rules={[{ required: true, message: '请输入部门名称' }]}>
            <Input placeholder="如 华东项目组" />
          </Form.Item>
          <Form.Item
            name="parentId"
            label="上级部门"
            extra="不选则为顶级部门。上级部门决定数据权限的覆盖范围，请谨慎调整"
          >
            <Select
              allowClear
              placeholder="顶级部门"
              options={(depts.data ?? [])
                .filter((d) => d.id !== editing?.id)
                .map((d) => ({ value: d.id, label: `${d.name}（${d.path}）` }))}
            />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序" initialValue={0} extra="数字越小越靠前">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          {editing ? (
            <Alert
              type="warning"
              showIcon
              message="移动部门会影响已有数据权限"
              description="把部门挂到新的上级后，持有「本部门及以下」权限的角色可见范围会随之变化，请确认业务影响后再保存。"
            />
          ) : null}
        </Form>
      </Modal>

      <ConfirmDanger
        open={deleteTarget !== null}
        title={`删除部门「${deleteTarget?.name ?? ''}」`}
        level="high"
        confirmText="确认删除"
        acknowledgeText="我已知晓：删除部门会影响数据权限与待办派发"
        description={
          <Space direction="vertical" size={4}>
            <span>
              该部门当前有 <strong>{deleteTarget ? userCountOf(deleteTarget.id) : 0}</strong> 名用户。
              删除前请先把用户改派到其它部门，否则他们会失去部门归属，数据权限将退回「仅本人」。
            </span>
            <span>若该部门下有子部门，子部门也会一并失去上级，请先处理子部门。</span>
          </Space>
        }
        reasonLabel="删除原因（必填，不少于 8 字）"
        reasonPresets={['项目已结项，项目组撤销', '组织架构调整，部门合并到其它部门']}
        onConfirm={(reason) => removeDept(reason)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
